import { isNil } from 'ramda';

import { OutstandingAgreementType } from '__generated__/GQL';
import getIsAgreementCompleted from 'common/bento/modules/Agreement/libs/getIsAgreementCompleted';
import { MachineGuards } from 'common/bento/types/MachineConfig';
import getCapitalDepositQuestionsModuleStatus, {
  CapitalDepositQuestionsModuleStatus,
} from 'features/Onboarding/modules/CapitalDepositQuestions/libs/getCapitalDepositQuestionsModuleStatus';
import getCompanyComplianceModuleStatus, {
  CompanyComplianceModuleStatus,
} from 'features/Onboarding/modules/CompanyCompliance/libs/getCompanyComplianceModuleStatus';
import { getMandateStatus } from 'features/Onboarding/modules/Mandate';
import { VerificationStatus } from 'features/Onboarding/modules/Verification/lib/steps/types';

import { Model } from './model';

export enum Guard {
  HasSignedAgreement = 'hasSignedAgreement',
  ShouldCreateCompany = 'shouldCreateCompany',
  ShouldAnswerCapitalDepositQuestions = 'shouldAnswerCapitalDepositQuestions',
  ShouldAnswerCompanyCompliance = 'shouldAnswerCompanyCompliance',
  ShouldSkipCompanyCompliance = 'shouldSkipCompanyCompliance',
  ShouldUploadMandate = 'shouldUploadMandate',
  ShouldRegisterBeneficiaries = 'shouldRegisterBeneficiaries',
  ShouldSubmitApplication = 'shouldSubmitApplication',
}

const guards: MachineGuards<Model, Guard> = {
  hasSignedAgreement: ({ getModuleData }) => {
    const { outstandingAgreements } = getModuleData().data.viewer;

    if (!outstandingAgreements) {
      return false;
    }

    return getIsAgreementCompleted({
      agreementType: OutstandingAgreementType.Cgu,
      outstandingAgreements,
    });
  },

  shouldAnswerCapitalDepositQuestions: ({ getModuleData }) => {
    const { company } = getModuleData().data.viewer;
    if (!company || !company.capitalDeposit) {
      return false;
    }

    return (
      getCapitalDepositQuestionsModuleStatus({
        ...company,
        capitalDeposit: company.capitalDeposit, // without destructuring, ts can't know capitalDeposit is not null
      }) === CapitalDepositQuestionsModuleStatus.ToDo
    );
  },

  shouldAnswerCompanyCompliance: ({ getModuleData }) => {
    const { shouldAskSanctionsDeclarationQuestions, viewer } =
      getModuleData().data;

    if (!viewer.company || isNil(shouldAskSanctionsDeclarationQuestions)) {
      return false;
    }

    const moduleStatus = getCompanyComplianceModuleStatus({
      companyProfile: viewer.company.profile,
      shouldAskSanctionsDeclarationQuestions,
    });

    return [
      CompanyComplianceModuleStatus.ToDo,
      CompanyComplianceModuleStatus.Refused,
    ].includes(moduleStatus);
  },

  shouldCreateCompany: ({ getModuleData }) =>
    !getModuleData().data.viewer.company?.profile.legalName,

  shouldRegisterBeneficiaries: ({ getModuleData }) => {
    return !getModuleData().data.viewer.company?.metadata
      .hasRegisteredAllBeneficiaries;
  },

  shouldSkipCompanyCompliance: ({ getModuleData }) => {
    const { shouldAskSanctionsDeclarationQuestions, viewer } =
      getModuleData().data;

    if (!viewer.company || isNil(shouldAskSanctionsDeclarationQuestions)) {
      return false;
    }

    return (
      getCompanyComplianceModuleStatus({
        companyProfile: viewer.company.profile,
        shouldAskSanctionsDeclarationQuestions,
      }) === CompanyComplianceModuleStatus.Unnecessary
    );
  },

  shouldSubmitApplication: ({ getModuleData }) => {
    return !getModuleData().data.viewer.companyUser
      ?.isAwaitingAccountValidation;
  },

  shouldUploadMandate: ({ getModuleData }) => {
    const { company } = getModuleData().data.viewer;
    if (!company) {
      return false;
    }

    return (
      getMandateStatus({
        isMandatee: true,
        kybDocuments: company.kybDocuments,
      }) === VerificationStatus.ToDo
    );
  },
};

export default guards;
